import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import ProjectNavigation from '@/components/ProjectNavigation';
import {
  NotebookBackground,
  PageSlide,
  DrawingAnimation,
  DoodleStar,
  DoodleArrow,
  DoodleLightbulb,
  DoodleSpiral,
  CoffeeStain,
  WashiTape,
  StickyNote,
  PaperClip,
  TapeMark,
} from '@/components/notebook';
import { useNotebookSounds } from '@/hooks/useNotebookSounds';
import { notebookPalette } from '@/lib/notebookTheme';

const projects = [
  {
    id: 'ai-terminal-course',
    title: 'AI Terminal Course',
    description: 'First-ever terminal-based AI engineering course. 20 hands-on modules teaching LLMs, RAG, tool calling, and deployment - all from your terminal.',
    tech: ['Node.js', 'AI/ML', 'CLI', 'Education'],
    link: 'https://www.aiterminalcourse.com/',
    color: '#6366F1',
    marginNote: 'Learn by doing!',
  },
  {
    id: 'mcp-jest',
    title: 'MCP Jest',
    description: 'First-ever MCP server testing framework. Jest-inspired syntax with JSON config, parallel execution, snapshot testing, and CI/CD integration.',
    tech: ['TypeScript', 'Jest', 'MCP', 'Testing'],
    link: 'https://mcp-jest.ddiy.diy/',
    color: '#FF6B6B',
    marginNote: 'Test everything!',
  },
  {
    id: 'omnimcp',
    title: 'OmniMCP',
    description: 'Universal MCP client with interactive playground. Type-safe, browser-ready, supports OpenAI & Anthropic with battle-tested error handling.',
    tech: ['TypeScript', 'MCP', 'OpenAI', 'Anthropic'],
    link: 'https://omnimcp.ddiy.diy/',
    storyLink: '/lab/omnimcp',
    color: '#4ECDC4',
    marginNote: 'Connect everything',
  },
  {
    id: 'agentx',
    title: 'AA Kit (AgentX)',
    description: 'Universal AI Agent Framework for the MCP Era. Build production-ready agents in 3 lines. Every agent auto-exposes as MCP server.',
    tech: ['TypeScript', 'AI Agents', 'MCP', 'Multi-Model'],
    link: 'https://agentx.ddiy.diy/',
    color: '#8B5CF6',
    marginNote: 'Agents made easy',
  },
  {
    id: 'ai-ui-kit',
    title: 'AI UI Kit',
    description: 'Beautiful, accessible React components for AI chat interfaces. WCAG 2.1 AA compliant with streaming support and dark mode.',
    tech: ['React', 'TypeScript', 'Accessibility', 'AI Chat'],
    link: 'https://aichatkit.ddiy.diy/',
    color: '#EC4899',
    marginNote: 'Pretty & accessible',
  },
  {
    id: 'genai-hooks',
    title: 'GenAI Hooks',
    description: 'React hooks for seamless LLM integration. Add AI capabilities to React apps with a single line of code. 500+ GitHub stars.',
    tech: ['React', 'TypeScript', 'LLMs', 'OpenAI API'],
    link: 'https://github.com/josharsh/genai-hooks',
    color: '#10B981',
    marginNote: '500+ stars!',
  },
  {
    id: 'sundae',
    title: 'Sundae',
    description: 'macOS voice assistant that turns speech into polished text. AI writing personas, universal app integration, and 100% local privacy.',
    tech: ['macOS', 'Speech Recognition', 'AI', 'Productivity'],
    link: 'https://sundaeai.vercel.app/',
    color: '#F59E0B',
    marginNote: 'Voice to text',
  },
  {
    id: 'ideabrew',
    title: 'IdeaBrew',
    description: 'AI-powered idea-to-blueprint tool. Voice capture to MVP specs in 2-3 minutes with GitHub integration and Claude Code prompts.',
    tech: ['AI', 'Voice', 'GitHub', 'Automation'],
    link: 'https://idea-brew.vercel.app/',
    color: '#14B8A6',
    marginNote: 'Ideas to reality',
  },
  {
    id: 'inflash',
    title: 'InFlash',
    description: 'macOS thought capture app. Option+Z triggers instant voice/text capture with local processing and Apple ecosystem sync.',
    tech: ['macOS', 'Voice', 'Privacy', 'Productivity'],
    link: 'https://inflash.vercel.app/',
    color: '#3B82F6',
    marginNote: 'Capture thoughts',
  },
  {
    id: 'clarity',
    title: 'Clarity for Gmail',
    description: 'AI email triage extension. Auto-categorizes to ACT_NOW, READ, FYI, NOISE. Local AI processing option for complete privacy.',
    tech: ['Chrome Extension', 'AI', 'Gmail', 'Privacy'],
    link: 'https://clarity-website-theta.vercel.app/',
    color: '#EF4444',
    marginNote: 'Inbox zero!',
  },
  {
    id: 'sketchflow',
    title: 'SketchFlow',
    description: 'Minimalist digital drawing canvas. Clean, simple interface for sketching and creative work without distractions.',
    tech: ['Canvas', 'Drawing', 'Minimalist', 'Creative'],
    link: 'https://sketchflow.ddiy.diy/',
    color: '#1A535C',
    marginNote: 'Just draw',
  },
  {
    id: 'ddiy',
    title: 'DDIY Agent Builder',
    description: 'No-code AI agent builder based on Autogen. Create multi-agent workflows without writing code.',
    tech: ['Python', 'Autogen', 'React', 'AI Orchestration'],
    link: 'https://github.com/josharsh/ddiy',
    color: '#FFE66D',
    marginNote: 'No-code agents',
  },
  {
    id: 'mcp-boilerplate',
    title: 'MCP Boilerplate',
    description: 'Production-ready boilerplate for building Model Context Protocol servers. Accelerates AI agent development.',
    tech: ['Python', 'MCP', 'PyPI', 'DevTools'],
    link: 'https://pypi.org/project/mcp-boilerplate/',
    color: '#FF9F1C',
    marginNote: 'Start fast!',
  },
  {
    id: 'tgsd-ai',
    title: "The 'Get Shit Done' AI",
    description: 'A comprehensive work operating system that cuts through the chaos. Contextual AI, real-time collaboration, adaptive workflows, and an expertise marketplace - all in one hub that thinks with you.',
    tech: ['AI', 'Productivity', 'Collaboration', 'Workflows'],
    link: 'https://www.loom.com/share/7fe82dfee6404292add0e6a33853b187',
    storyLink: '/lab/learning-ai',
    color: '#7C3AED',
    marginNote: 'Get shit done!',
  },
];

// Sketchy SVG Components with drawing animation support
const SketchyTerminal = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M25,35 Q28,32 175,30 Q180,33 178,155 Q175,160 25,158 Q20,155 25,35" fill={color} opacity="0.15" />
    <path d="M22,33 C30,28 170,25 180,32 C185,40 183,150 178,158 C170,165 30,168 22,160 C15,152 18,42 22,33" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <rect x="38" y="48" width="125" height="85" rx="3" fill={color} opacity="0.08" />
    <path d="M50,65 Q52,64 58,66 L85,66" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6" />
    <path d="M50,82 Q53,80 60,83 L110,82" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    <path d="M50,99 Q51,97 55,100 L75,99" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.4" />
    <rect x="80" y="95" width="8" height="12" fill={color} opacity="0.7" className="animate-pulse" />
    <path d="M85,162 Q100,175 115,162" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" />
    <path d="M70,180 Q100,178 130,180" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

const SketchyTestFramework = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M70,40 Q68,38 72,35 L88,35 Q92,38 90,40 L95,140 Q98,165 100,170 Q85,180 60,170 Q62,165 65,140 Z" fill={color} opacity="0.12" />
    <path d="M68,38 C65,35 75,32 90,35 C95,38 93,42 95,60 L100,145 C103,165 90,178 75,178 C60,178 50,165 55,145 L62,60 C63,42 66,40 68,38" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <path d="M62,120 Q75,115 88,122 Q92,140 90,155 Q80,165 70,160 Q58,150 62,120" fill={color} opacity="0.25" />
    <circle cx="72" cy="135" r="4" fill={color} opacity="0.4" />
    <circle cx="80" cy="145" r="3" fill={color} opacity="0.3" />
    <path d="M125,50 L135,65 L160,30" stroke={color} strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="145" cy="100" r="8" stroke={color} strokeWidth="2" fill={color} opacity="0.2" />
    <circle cx="165" cy="115" r="6" stroke={color} strokeWidth="2" fill={color} opacity="0.15" />
    <path d="M150,105 L160,112" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SketchyNetwork = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M100,70 Q130,65 140,100 Q135,135 100,140 Q65,135 60,100 Q65,65 100,70" fill={color} opacity="0.2" />
    <path d="M100,68 C125,60 145,80 142,105 C140,130 120,145 100,142 C80,140 58,125 58,100 C58,75 80,62 100,68" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="100" cy="30" r="12" fill={color} opacity="0.25" stroke={color} strokeWidth="2" />
    <circle cx="165" cy="80" r="10" fill={color} opacity="0.2" stroke={color} strokeWidth="2" />
    <circle cx="155" cy="150" r="11" fill={color} opacity="0.22" stroke={color} strokeWidth="2" />
    <circle cx="45" cy="145" r="9" fill={color} opacity="0.18" stroke={color} strokeWidth="2" />
    <circle cx="35" cy="75" r="13" fill={color} opacity="0.23" stroke={color} strokeWidth="2" />
    <path d="M100,70 Q102,50 100,42" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="4 3" />
    <path d="M138,95 Q150,88 155,82" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="4 3" />
    <circle cx="100" cy="105" r="15" fill={color} opacity="0.15" className="animate-pulse" />
  </svg>
);

const SketchyAgent = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M65,35 Q68,30 135,32 Q142,38 140,95 Q138,102 65,100 Q58,95 65,35" fill={color} opacity="0.15" />
    <path d="M63,33 C70,25 130,25 140,33 C148,42 145,92 140,100 C132,108 68,108 60,100 C52,92 55,42 63,33" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <ellipse cx="82" cy="60" rx="10" ry="12" fill={color} opacity="0.3" />
    <ellipse cx="118" cy="62" rx="11" ry="11" fill={color} opacity="0.3" />
    <circle cx="85" cy="58" r="4" fill={color} opacity="0.8" />
    <circle cx="120" cy="60" r="4" fill={color} opacity="0.8" />
    <path d="M100,32 Q98,18 102,10" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
    <circle cx="102" cy="8" r="5" fill={color} opacity="0.4" stroke={color} strokeWidth="1.5" />
    <path d="M70,105 Q65,110 68,160 Q75,175 125,173 Q135,168 132,105 Q130,100 70,105" fill={color} opacity="0.12" />
    <path d="M68,103 C60,108 62,155 70,168 C82,180 118,180 130,168 C140,155 142,108 132,103" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const SketchyChatBubbles = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M30,50 Q28,40 120,38 Q135,42 138,90 Q136,105 45,108 L35,130 L45,105 Q25,102 30,50" fill={color} opacity="0.18" />
    <path d="M28,48 C25,35 115,30 130,40 C142,50 140,95 130,105 C115,115 50,118 40,110 L30,135 L42,108 C30,105 28,60 28,48" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <path d="M45,60 Q48,58 95,60" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.4" />
    <path d="M45,75 Q50,73 80,75" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.35" />
    <path d="M85,125 Q90,118 170,120 Q180,125 178,158 Q175,168 95,170 Q82,165 85,125" fill={color} opacity="0.12" />
    <path d="M88,123 C92,115 165,112 175,122 C185,132 182,160 172,168 C162,176 98,178 88,168 C78,158 82,132 88,123" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <circle cx="115" cy="145" r="5" fill={color} opacity="0.5" className="animate-pulse" />
    <circle cx="132" cy="145" r="5" fill={color} opacity="0.4" className="animate-pulse" style={{ animationDelay: '0.2s' }} />
    <circle cx="149" cy="145" r="5" fill={color} opacity="0.3" className="animate-pulse" style={{ animationDelay: '0.4s' }} />
  </svg>
);

const SketchyHook = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M90,20 Q88,18 92,15 L108,15 Q112,18 110,25 L110,90 Q115,140 145,150 Q165,148 170,125 Q172,105 155,100" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" />
    <path d="M95,25 L105,25 L105,85 Q108,130 140,142 Q158,140 162,120" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.3" />
    <path d="M25,60 L15,80 L25,100" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" />
    <path d="M55,60 L65,80 L55,100" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" />
    <path d="M30,75 Q35,73 48,76" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    <ellipse cx="100" cy="160" rx="45" ry="15" stroke={color} strokeWidth="2" fill="none" opacity="0.3" transform="rotate(-20 100 160)" />
    <circle cx="60" cy="155" r="6" fill={color} opacity="0.4" />
  </svg>
);

const SketchySoundWave = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M85,60 Q80,55 80,95 Q78,105 85,110 Q95,115 105,115 Q115,112 118,105 Q122,95 120,60 Q118,50 100,48 Q85,50 85,60" fill={color} opacity="0.2" />
    <path d="M83,58 C78,50 78,100 82,110 C88,120 112,120 118,110 C124,100 124,50 118,55 C112,45 88,45 83,58" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <path d="M100,118 L100,145" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <path d="M75,145 Q100,150 125,145" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" />
    <path d="M130,80 Q145,80 150,100 Q145,120 130,120" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.5" />
    <path d="M145,70 Q168,75 172,100 Q168,125 145,130" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.35" />
    <path d="M160,58 Q190,68 195,100 Q190,132 160,142" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.2" />
  </svg>
);

const SketchyIdeaBulb = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M100,20 Q145,25 155,70 Q158,100 140,125 Q130,142 125,150 L75,150 Q70,142 60,125 Q42,100 45,70 Q55,25 100,20" fill={color} opacity="0.15" />
    <path d="M100,18 C140,20 158,55 158,80 C158,110 138,135 128,150 L72,150 C62,135 42,110 42,80 C42,55 60,20 100,18" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <path d="M85,90 Q100,60 115,90 Q105,100 95,100 Q85,100 85,90" stroke={color} strokeWidth="2" fill={color} opacity="0.3" strokeLinecap="round" />
    <path d="M73,153 L127,153 L125,175 L75,175 Z" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M100,5 L100,0" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    <path d="M140,30 L148,22" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.4" />
    <path d="M60,30 L52,22" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.4" />
  </svg>
);

const SketchyFlash = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M115,15 L70,95 L100,95 L60,185 L140,85 L105,85 Z" fill={color} opacity="0.2" />
    <path d="M118,12 L65,92 L98,92 L55,190 L145,82 L108,82 Z" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M110,35 L78,90 L95,90 L72,160" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4" />
    <circle cx="100" cy="100" r="70" stroke={color} strokeWidth="2" fill="none" strokeDasharray="8 6" opacity="0.3" />
    <path d="M25,25 L25,50 M25,25 L50,25" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
    <path d="M175,25 L175,50 M175,25 L150,25" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
    <path d="M25,175 L25,150 M25,175 L50,175" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
    <path d="M175,175 L175,150 M175,175 L150,175" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
  </svg>
);

const SketchyEmailBrain = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M20,55 Q18,50 180,50 Q185,55 185,145 Q182,152 20,150 Q15,145 20,55" fill={color} opacity="0.12" />
    <path d="M18,53 C15,48 185,45 188,53 C192,62 190,145 185,152 C178,160 22,160 15,152 C8,145 12,62 18,53" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <path d="M20,55 L100,105 L180,55" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M100,25 Q120,20 130,35 Q140,32 145,45 Q152,50 148,65 Q155,75 145,85 Q148,95 135,98 Q130,108 115,105 Q105,115 90,105 Q75,110 70,98 Q55,100 55,85 Q45,78 52,65 Q48,52 60,45 Q58,32 75,30 Q85,18 100,25" fill={color} opacity="0.2" />
    <path d="M100,23 C115,18 128,22 132,35 C142,30 148,42 148,52 C158,55 155,70 148,78 C155,88 148,98 138,100 C135,110 120,112 108,108 C100,118 82,115 75,105 C62,112 52,102 55,90 C42,85 45,70 55,62 C48,50 58,38 72,38 C72,25 88,18 100,23" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <circle cx="40" cy="135" r="8" fill={color} opacity="0.3" />
    <circle cx="70" cy="135" r="8" fill={color} opacity="0.25" />
    <circle cx="100" cy="135" r="8" fill={color} opacity="0.2" />
    <circle cx="130" cy="135" r="8" fill={color} opacity="0.15" />
  </svg>
);

const SketchyPencilFlow = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M35,165 L55,145 L155,45 Q165,35 175,45 L180,50 Q190,60 180,70 L80,170 L60,175 Z" fill={color} opacity="0.15" />
    <path d="M33,168 L52,148 L152,48 C160,40 175,40 182,48 L185,52 C193,60 193,75 185,82 L85,182 L55,188 Z" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <path d="M35,165 L55,145 L62,152 L42,172 Z" fill={color} opacity="0.3" />
    <path d="M162,58 L175,45 Q182,40 188,48 L175,62 Z" fill={color} opacity="0.25" />
    <path d="M45,140 Q30,120 50,100 Q75,80 60,55 Q50,35 70,25" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4" />
    <circle cx="90" cy="30" r="12" stroke={color} strokeWidth="1.5" fill={color} opacity="0.15" strokeDasharray="4 3" />
  </svg>
);

const SketchyBlocks = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M30,140 L90,110 L170,140 L110,170 Z" fill={color} opacity="0.2" />
    <path d="M30,140 L30,165 L110,195 L110,170 Z" fill={color} opacity="0.15" />
    <path d="M110,170 L110,195 L170,165 L170,140 Z" fill={color} opacity="0.25" />
    <path d="M28,138 L88,108 L172,138 L112,168 Z M28,138 L28,168 L112,198 L112,168 M112,168 L112,198 L172,168 L172,138" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M50,95 L100,70 L150,95 L100,120 Z" fill={color} opacity="0.18" />
    <path d="M50,95 L50,115 L100,140 L100,120 Z" fill={color} opacity="0.12" />
    <path d="M100,120 L100,140 L150,115 L150,95 Z" fill={color} opacity="0.22" />
    <path d="M48,93 L98,68 L152,93 L102,118 Z M48,93 L48,118 L102,143 L102,118 M102,118 L102,143 L152,118 L152,93" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M65,55 L100,40 L135,55 L100,70 Z" fill={color} opacity="0.15" />
    <path d="M63,53 L98,38 L137,53 L102,68 Z M63,53 L63,73 L102,88 L102,68 M102,68 L102,88 L137,73 L137,53" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M170,50 L170,70 M160,60 L180,60" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.4" />
    <path d="M25,80 L25,95 M18,87 L32,87" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.35" />
  </svg>
);

const SketchyBlueprint = ({ color }: { color: string }) => (
  <svg viewBox="0 0 200 200" className="w-full h-full">
    <path d="M25,25 Q30,20 175,22 Q182,28 180,175 Q175,182 28,180 Q20,175 25,25" fill={color} opacity="0.08" />
    <path d="M23,23 C28,15 172,15 180,23 C188,32 185,172 178,180 C170,188 30,188 22,180 C14,172 18,32 23,23" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <path d="M40,25 L40,175" stroke={color} strokeWidth="1" opacity="0.15" />
    <path d="M70,25 L70,175" stroke={color} strokeWidth="1" opacity="0.15" />
    <path d="M100,25 L100,175" stroke={color} strokeWidth="1" opacity="0.15" />
    <path d="M130,25 L130,175" stroke={color} strokeWidth="1" opacity="0.15" />
    <path d="M25,50 L175,50" stroke={color} strokeWidth="1" opacity="0.15" />
    <path d="M25,80 L175,80" stroke={color} strokeWidth="1" opacity="0.15" />
    <path d="M25,110 L175,110" stroke={color} strokeWidth="1" opacity="0.15" />
    <rect x="45" y="55" width="40" height="25" rx="3" fill={color} opacity="0.2" stroke={color} strokeWidth="1.5" />
    <rect x="115" y="55" width="45" height="25" rx="3" fill={color} opacity="0.15" stroke={color} strokeWidth="1.5" />
    <path d="M85,68 L115,68" stroke={color} strokeWidth="2" strokeLinecap="round" strokeDasharray="4 3" />
    <path d="M65,80 L65,110" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M137,80 L137,115" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const getShape = (index: number, color: string) => {
  const shapes = [
    SketchyTerminal, SketchyTestFramework, SketchyNetwork, SketchyAgent,
    SketchyChatBubbles, SketchyHook, SketchySoundWave, SketchyIdeaBulb,
    SketchyFlash, SketchyEmailBrain, SketchyPencilFlow, SketchyBlocks,
    SketchyBlueprint,
  ];
  const Component = shapes[index % shapes.length];
  return <Component color={color} />;
};

const Projects = () => {
  const [showIntro, setShowIntro] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const navigate = useNavigate();
  const { playPageFlip, playPencilScratch, playClick } = useNotebookSounds();

  const handleNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % projects.length);
    playPageFlip();
  }, [playPageFlip]);

  const handlePrev = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
    playPageFlip();
  }, [playPageFlip]);

  const handleNavigate = useCallback((index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
    playPageFlip();
  }, [currentIndex, playPageFlip]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showIntro) {
        if (e.key === 'Enter') {
          playClick();
          setShowIntro(false);
        }
        return;
      }

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        handleNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        handlePrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showIntro, handleNext, handlePrev, playClick]);

  const currentProject = projects[currentIndex];

  // Intro/Landing Screen - Sketchbook Cover
  if (showIntro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f5f0e6] via-[#ebe5d9] to-[#e0d9cc] flex flex-col relative overflow-hidden">
        {/* Grid paper texture */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `
              linear-gradient(#d1e0ed40 1px, transparent 1px),
              linear-gradient(90deg, #d1e0ed40 1px, transparent 1px)
            `,
            backgroundSize: '24px 24px',
          }}
        />

        {/* Ring binder spine */}
        <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-[#8b7355] to-[#a08060] shadow-lg">
          <div className="absolute inset-y-0 left-0 right-0 flex flex-col justify-around py-12">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="mx-auto w-8 h-8 rounded-full bg-gray-300 border-2 border-gray-400"
                style={{ boxShadow: 'inset 2px 2px 4px rgba(0,0,0,0.2)' }}
              />
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="relative z-10 flex items-center justify-between px-8 py-6 ml-12">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium font-typewriter">Back</span>
          </motion.button>
        </div>

        {/* Main Content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 md:px-16 ml-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-8 max-w-3xl"
          >
            {/* Washi tape badge */}
            <motion.div
              initial={{ opacity: 0, rotate: -5 }}
              animate={{ opacity: 1, rotate: 2 }}
              transition={{ delay: 0.2 }}
            >
              <WashiTape color="blue" rotation={-2}>
                <span className="font-handwritten">{projects.length} projects shipped</span>
              </WashiTape>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-6xl md:text-8xl font-sketch text-gray-800 tracking-tight"
            >
              Side Projects
            </motion.h1>

            {/* Subtitle handwritten */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="font-handwritten text-2xl md:text-3xl text-gray-600"
            >
              weekend experiments that became real products
            </motion.p>

            {/* Philosophy sticky note */}
            <motion.div
              initial={{ opacity: 0, rotate: -10 }}
              animate={{ opacity: 1, rotate: -2 }}
              transition={{ delay: 0.5, type: 'spring' }}
              className="inline-block"
            >
              <StickyNote color="green" size="medium" rotation={-2}>
                <span className="font-handwritten">
                  Ship fast,<br/>
                  solve real problems
                </span>
              </StickyNote>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="pt-8"
            >
              <button
                onClick={() => {
                  playClick();
                  setShowIntro(false);
                }}
                className="group inline-flex items-center gap-4 px-8 py-4 bg-gray-800 text-white font-sketch rounded-sm hover:bg-gray-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Browse Projects</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Doodle decorations */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 1 }}
          className="absolute top-1/4 right-[15%]"
        >
          <DoodleLightbulb size={48} color={notebookPalette.pencil.light} animated delay={1.2} />
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-1/4 left-[20%]"
        >
          <DoodleSpiral size={40} color={notebookPalette.pencil.light} animated delay={1.4} />
        </motion.div>

        {/* Coffee stain */}
        <CoffeeStain
          size="large"
          intensity="light"
          position={{ bottom: '20%', right: '8%' }}
        />

        {/* Hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-400 text-xs font-typewriter z-10 ml-6"
        >
          press enter to start
        </motion.p>
      </div>
    );
  }

  // Main Carousel - Sketchbook Pages
  return (
    <div className="min-h-screen bg-[#f0ebe3] relative overflow-hidden">
      {/* Paper texture */}
      <div className="absolute inset-0 paper-aged" />

      {/* Ring binder spine */}
      <div className="hidden md:block absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-[#8b7355] to-[#a08060] shadow-lg z-30">
        <div className="absolute inset-y-0 left-0 right-0 flex flex-col justify-around py-8">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="mx-auto w-6 h-6 rounded-full bg-gray-300 border-2 border-gray-400"
              style={{ boxShadow: 'inset 2px 2px 3px rgba(0,0,0,0.2)' }}
            />
          ))}
        </div>
      </div>

      {/* Navigation Header */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-8 py-4 bg-gradient-to-b from-[#f0ebe3] to-transparent md:pl-16">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium font-typewriter">Back</span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-typewriter text-gray-500 text-sm"
        >
          {String(currentIndex + 1).padStart(2, '0')} / {String(projects.length).padStart(2, '0')}
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen flex items-center justify-center px-4 md:px-8 md:pl-16 py-20">
        <div className="w-full max-w-5xl">
          <PageSlide
            pageKey={currentProject.id}
            direction={direction}
            className="w-full"
            onFlipComplete={() => playPencilScratch()}
          >
            <NotebookBackground
              variant={currentIndex % 2 === 0 ? 'grid' : 'dotted'}
              marginLine={false}
              holes={false}
              aging="light"
              className="min-h-[75vh] rounded-lg shadow-2xl"
            >
              <div className="p-6 md:p-10 lg:p-12 relative">
                {/* Paper clip decoration */}
                {currentIndex % 4 === 0 && (
                  <PaperClip color="blue" position="top-right" rotation={-10} />
                )}

                {/* Tape mark */}
                {currentIndex % 3 === 1 && (
                  <div className="absolute top-4 left-4">
                    <TapeMark variant="washi" width={60} rotation={-15} />
                  </div>
                )}

                {/* Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  {/* Left - Illustration */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="relative"
                  >
                    <DrawingAnimation duration={1.2} trigger="immediate">
                      <div className="w-full aspect-square max-w-md mx-auto">
                        {getShape(currentIndex, currentProject.color)}
                      </div>
                    </DrawingAnimation>

                    {/* Margin note */}
                    {currentProject.marginNote && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="absolute -bottom-4 left-1/2 -translate-x-1/2"
                      >
                        <span className="font-handwritten text-sm text-gray-500 italic bg-white/50 px-2 py-1 rounded">
                          * {currentProject.marginNote}
                        </span>
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Right - Content */}
                  <div className="space-y-6">
                    {/* Title */}
                    <motion.h1
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-4xl md:text-5xl font-sketch text-gray-800 leading-tight"
                    >
                      {currentProject.title}
                    </motion.h1>

                    {/* Description */}
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-lg text-gray-700 leading-relaxed font-serif"
                    >
                      {currentProject.description}
                    </motion.p>

                    {/* Tech tags */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="flex flex-wrap gap-2"
                    >
                      {currentProject.tech.map((tech, i) => (
                        <span
                          key={tech}
                          className="px-3 py-1.5 rounded-full text-sm font-handwritten bg-white/80 text-gray-700 border border-gray-200 shadow-sm"
                          style={{
                            transform: `rotate(${(i - 1) * 1.5}deg)`,
                            borderColor: currentProject.color + '40',
                          }}
                        >
                          {tech}
                        </span>
                      ))}
                    </motion.div>

                    {/* CTA */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="pt-4 flex flex-wrap gap-3"
                    >
                      <Button
                        size="lg"
                        className="bg-gray-800 text-white hover:bg-gray-700 rounded-sm font-sketch"
                        onClick={() => {
                          playClick();
                          window.open(currentProject.link, '_blank');
                        }}
                      >
                        View Project
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </Button>
                      {currentProject.storyLink && (
                        <Button
                          size="lg"
                          variant="outline"
                          className="rounded-sm font-sketch border-gray-300 hover:bg-gray-100"
                          onClick={() => {
                            playClick();
                            navigate(currentProject.storyLink!);
                          }}
                        >
                          Know More
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      )}
                    </motion.div>
                  </div>
                </div>

                {/* Coffee stain (occasional) */}
                {currentIndex % 5 === 3 && (
                  <CoffeeStain
                    size="small"
                    intensity="light"
                    position={{ bottom: '5%', left: '10%' }}
                  />
                )}
              </div>
            </NotebookBackground>
          </PageSlide>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="fixed bottom-6 left-0 right-0 px-8 flex items-center justify-between z-40 md:pl-16">
        {/* Page Indicators */}
        <div className="flex items-center gap-2">
          {projects.map((_, index) => (
            <button
              key={index}
              onClick={() => handleNavigate(index)}
              className={`transition-all duration-300 ${
                index === currentIndex
                  ? 'w-8 h-2 bg-gray-700 rounded-full'
                  : 'w-2 h-2 bg-gray-400 rounded-full hover:bg-gray-500'
              }`}
            />
          ))}
        </div>

        {/* Arrow Controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrev}
            className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-gray-900 hover:shadow-lg transition-all border border-gray-200"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={handleNext}
            className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-gray-900 hover:shadow-lg transition-all border border-gray-200"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Keyboard hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 text-gray-400 text-xs font-typewriter hidden md:block md:ml-5"
      >
        ← → to browse • Enter to view
      </motion.div>

      {/* Project Navigation Sidebar */}
      <ProjectNavigation
        activeIndex={currentIndex}
        onNavigate={handleNavigate}
      />
    </div>
  );
};

export default Projects;
